import { ServiceAvailabilities } from '../service-availabilities/service-availabilities'
import { useEffect, useState } from 'react'
import { useConfig, useCreateService, useCurrentShop, useService, useUpdateService } from 'old/api'
import { Feature, FlexStartInterval, Service, ServiceFormSchema, Shop }  from 'old/types'
import { CloudUploadOutlined } from '@ant-design/icons'
import { setIn } from 'final-form'
import { Loading, PageContentWrapper, PageError, VerticalContainer } from 'old/components/styled'
import { getShopId, queryClient, useTranslate, useWindowDimensions }  from 'old/utils'
import { useNavigate, useParams } from 'react-router-dom'
import { message, theme } from 'antd'
import { Form } from 'react-final-form'
import { PageHeader } from 'old/components/page-header/page-header'
import { ServiceTeamMembers } from 'old/components/service/service-team-members/service-team-members'
import { ServiceDuration } from 'old/components/service/service-duration/service-duration'
import { ServiceAppointment } from 'old/components/service/service-appointment/service-appointment'
import { ServiceOtherOptions } from 'old/components/service/service-other-options/service-other-options'
import { ServiceGroupAppointment } from 'old/components/service/service-group-appointment/service-group-appointment'
import { ServiceRemove } from 'old/components/service/service-remove/service-remove'
import { ServiceBufferTime } from 'old/components/service/service-buffer-time/service-buffer-time'
import { ServiceStorefront } from 'old/components/service/service-storefront/service-storefront'

export const ServiceForm = () => {
    const params = useParams()
    return params.id ? <EditService id={params.id}/> : <ServiceFormController service={null}/>
}

type EditServiceProps = {
    id: string
}

const EditService = ({ id }: EditServiceProps) => {

    //navigate:
    const navigate = useNavigate()
    
    //service:
    const { data: service, isLoading } = useService(id, undefined, () => navigate('/service'))

    //redirect if not found:
    useEffect(() => {
        if(service === null || service === false){
            navigate('/service')
        }
    }, [isLoading])

    //render:
    return isLoading ? <Loading/> : !service ? <PageError/> : <ServiceFormController service={service}/>

}

type ServiceControllerProps = {
    service: Service | null
}

const ServiceFormController = ({ service }: ServiceControllerProps) => {

    const isCreate = !service

    //hooks:
    const { token: { padding, margin, marginLG } } = theme.useToken()
    const { SM } = useWindowDimensions()
    const { __ } = useTranslate()
    const { data: config } = useConfig()
    const { data: shop } = useCurrentShop()
    const navigate = useNavigate()

    //state:
    const [ initialValues, setInitialValues ] = useState(service ?? getDefaultValues(shop || null))
    const [ savingValues, setSavingValues ] = useState(service ?? getDefaultValues(shop || null))

    //mutation:
    const { mutate: createService, isLoading: isCreateLoading, data: createResponse } = useCreateService(savingValues)
    const { mutate: updateService, isLoading: isUpdateLoading, data: updateResponse } = useUpdateService(savingValues)

    //create:
    const create = (values: Service) => {
        setSavingValues(values)
        createService()
    }
    const update = (values: Service) => {
        setSavingValues(values)
        updateService()
    }

    //after create:
    useEffect(() => {
        if(createResponse){
            queryClient.invalidateQueries(['services', getShopId()])
            navigate(`/service`)
        }
    }, [isCreateLoading])

    //after update:
    useEffect(() => {
        if(updateResponse){
            setInitialValues({...savingValues})
            queryClient.invalidateQueries(['service', savingValues.id])
            queryClient.invalidateQueries(['services', getShopId()])
            queryClient.invalidateQueries(['teamMembers', getShopId()])
            initialValues.teamMembers.map(teamMember => {
                queryClient.resetQueries(['teamMember', teamMember.id])
            })
            savingValues.teamMembers.map(teamMember => {
                queryClient.resetQueries(['teamMember', teamMember.id])
            })
            message.success(__`service_updated`)
        }
    }, [isUpdateLoading])

    return !config ? <PageError/> : (
        <PageContentWrapper>
            <Form
                initialValues={initialValues}
                subscription={{ dirty: true }}
                validate={validateFormValues(ServiceFormSchema(__))}
                onSubmit={values => ServiceFormSchema(__).validate(values).then(
                    data => isCreate ? create(data as Service) : update(data as Service)
                )}
                render={({ form, dirty, handleSubmit,  }) => (

                    <VerticalContainer style={{ gap: margin }}>

                        {/* header */}
                        <PageHeader
                            title={isCreate ? __`create_service` : initialValues.title}
                            description={isCreate ? __`create_new_service_and_add_a_resource_to_it` : __`change_your_service_settings_and_save_it`}
                            primaryButton={{
                                name: isCreate ? __`create` : __`save`,
                                icon: <CloudUploadOutlined/>,
                                mode: (isCreateLoading || isUpdateLoading) ? 'loading' : dirty ? 'normal': 'disabled',
                                onClick: handleSubmit
                            }}
                            secondaryButton={dirty ? {
                                name: __`discard`,
                                mode: (isCreateLoading || isUpdateLoading) ? 'disabled' : 'normal',
                                onClick: () => form.reset()
                            } : undefined}
                            backButton={{
                                name: __`back`,
                                onClick: () => navigate('/service')
                            }}
                            isFormDirty={dirty}
                        />

                        <VerticalContainer style={{ padding: SM ? `0 ${padding}px` : 0, gap: marginLG }}>

                            {/* members */}
                            <ServiceTeamMembers/>

                            {/* duration */}
                            <ServiceDuration/>

                            {/* appointment details */}
                            <ServiceAppointment/>

                            {/* availabilities */}
                            {config.features.includes(Feature.SERVICE_HOURS) && (
                                <ServiceAvailabilities/>
                            )}

                            {/* buffer time */}
                            <ServiceBufferTime/>

                            {/* group appointments */}
                            {<ServiceGroupAppointment/>}

                            {/* storefront */}
                            <ServiceStorefront/>

                            {/* other options */}
                            <ServiceOtherOptions/>

                            {/* remove */}
                            {!isCreate && <ServiceRemove/>}

                        </VerticalContainer>
                    
                    </VerticalContainer>
                    
                )}
            />
        </PageContentWrapper>
    )
    
}

const getDefaultValues = (shop: Shop | null): Service => ({
    
    id: '',
    title: '',
    image: {
        id: null,
        path: null
    },

    teamMembers: [],
    variants: [],

    location: {
        content: null
    },
    eventDescription: null,
  
    availabilities: [],

    bufferTimeBefore: 0,
    bufferTimeAfter: 0,
    bufferFromNow: 0,

    groupAppointments: false,
    groupAppointmentSlots: null,

    storefrontStartDate: null,
    instantBooking: false,
    displayTeamOnStorefront: false,

    disableSameDayBooking: false,
    flexStartInterval: (shop && shop.isMigrated) ? FlexStartInterval.FIFTEEN : undefined,
    roundStartInterval: 0,
    bookingUntilDays: null,

    customerCanCancel: false,
    customerCanReschedule: false,
    customerCanManageBefore: null,
    refundMoneyOnCancel: false

})

//validateFormValues:
const validateFormValues = (schema: any) => async (values: Service) => {
    if(typeof schema === 'function')
        schema = schema()
    try{
        await schema.validate(values, { abortEarly: false })
    }catch(err: any){
        const errors = err.inner.reduce((formError: object, innerError: any) => {
            return setIn(formError, innerError.path, innerError.message)
        }, {})
        return errors
    }
}
